import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { DocumentService } from 'src/app/shared/document.service';
import { ContextService } from 'src/core/services/context.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { DocumentGenerationView } from './document-generation.view';
import { DocumentGeneratorView } from './document-generator.view';
import { FileSystemListDataService } from './file-system-list.data';
import { EmailDocumentService } from 'src/app/general/email-document-template/email-document.service';
import { CommunicationService } from 'src/core/services/communication.service';

interface IDocumentGenerationControllerProps {
	usage: DocumentGenerationUsage;
	marketId?: number;
	brandId?: number;
	targetId: number;
	containerId: string;
	contextService?: ContextService;
	setBusy: (isBusy: boolean) => void;
	generationEnabled?: boolean;
	mode?: ControlMode;
}

@observer
export class DocumentGenerationController extends React.Component<IDocumentGenerationControllerProps, any> {
	@resolve
	private documentService: DocumentService;

	@resolve
	private emailService: EmailDocumentService;

	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: FileSystemListDataService;

	@resolve
	private communicationService: CommunicationService;

	public componentDidMount() {
		this.dataService = FileSystemListDataService.make();
		this.communicationService.EventEmitter.on("entityChange", this.onEntityChange.bind(this));
		this.loadData();
	}

	public componentWillUnmount() {
		this.communicationService.EventEmitter.removeListener("entityChange", this.onEntityChange.bind(this));
	}

	public render() {
		if (this.dataService == null) {
			return null;
		}

		return (
			<DocumentGenerationView
				model={this.dataService}
				downloadDocument={this.downloadDocument}
				regenerateDocument={this.regenerateDocument}
				setBusy={this.props.setBusy}
				usages={this.props.usage}
				targetId={this.props.targetId}
				sendEmailDocument={this.sendEmailDocument}
			>
				<DocumentGeneratorView
					setBusy={this.props.setBusy}
					targetId={this.props.targetId}
					usage={this.props.usage}
					generationEnabled={this.props.generationEnabled}
					afterGenerate={() => this.loadData()}
					mode={this.props.mode}
					marketId={this.props.marketId}
					brandId={this.props.brandId}
				/>
			</DocumentGenerationView>
		);
	}

	private async loadData(): Promise<void> {
		this.dataService.addFilter('containerId', 'eq', this.props.containerId);
		this.dataService.addFilter('isLatestVersion', 'eq', true);
		this.dataService.addSorting('id', 'asc', true);
		this.dataService.pageSize = 25;

		this.props.setBusy(true);
		try {
			await this.dataService.load(query => query);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			this.props.setBusy(false);
		}
	}

	private downloadDocument = (fileSystem: IFileSystemSummary): void => {
		this.documentService.downloadDocument(fileSystem.containerId, fileSystem.id, fileSystem.filename);
	}

	private sendEmailDocument = (fileSystem: IFileSystemSummary, template: number): void => {
		if (template != 0) {
			this.emailService.sendDocumentEmail([this.props.targetId],
				[fileSystem.id], template, this.props.usage).then((result) => {
					this.toast.showToast("Send document", "The document was succesfully sent", 'success');
				});
		}
	}

	private regenerateDocument = async (fileSystem: IFileSystemSummary): Promise<void> => {
		this.props.setBusy(true);

		// TODO: regeneration stuff

		this.props.setBusy(false);
	}

	private onEntityChange = (data: EntityChange) => {
		if (data.TypeName == 'FileSystemSummary' && data.Operation === 0) {
			this.toast.showToast("Document generated!", `Document ${data.Payload} has finish generation.`, "success");
			this.dataService.reload();
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/general/document-generation/document-generation.controller.tsx